// ============================================================
// Ancestral Rune Tree v2 — Three-Branch Talent System
// ============================================================
// WRATH    (war path)     — offense: damage / crit / spell power
// WARD     (defense path) — survivability: HP / regen / shields / resist
// CUNNING  (fortune path) — utility: gold / runes / movement / rerolls
//
// 72 nodes total (24 per branch). Tier-gated by stage cleared
// AND ranks spent in branch. Hard player budget = 90 ranks
// (~38% of ~238 max ranks) so 3-elite builds are impossible.
// Elite gate: stage 30 + 30 ranks spent in branch.
//
// Reset cost: 120 runes (refunds every rank).
// ============================================================
// ── Branch identity ──
export const BRANCH_INFO = {
    war: { name: 'WRATH', color: 0xc03020, icon: 'ᚹ' },
    defense: { name: 'WARD', color: 0x4a7090, icon: '⛨' },
    fortune: { name: 'CUNNING', color: 0xb07820, icon: 'ᚠ' },
};
// ── Hard caps ──
/** Total ranks the player may spend across the entire tree.
 *  Lowered 90 → 80 per user feedback so the build forces tighter choices. */
export const MAX_TREE_POINTS = 80;
/** Cost in runes to refund every rank in the tree.
 *  Beta-build value: 0 — testers respec freely so they can sample every
 *  branch of the v2 tree without grinding for runes. Bump back to a
 *  meaningful number (120 was the v2 launch tuning) before player
 *  release if respecs should re-acquire a real cost. */
export const RUNE_TREE_RESET_COST = 0;
// ── Layout constants (3-column grid for the v2 UI) ──
const WRATH_X = 320;
const WARD_X = 960;
const CUNNING_X = 1600;
const T1_Y = 140;
const T2_Y = 260;
const T3_Y = 400;
const T4_Y = 540;
const ELITE_Y = 720;
/** Spread N nodes horizontally within a branch column. idx is 0..total-1. */
const spread = (centerX, idx, total, span = 360) => {
    if (total <= 1)
        return centerX;
    return Math.round(centerX - span / 2 + (idx * span) / (total - 1));
};
// Tier gates
const T2_STAGE = 5;
const T3_STAGE = 10;
const T4_STAGE = 20;
const ELITE_STAGE = 30;
const T2_RANKS = 5;
const T3_RANKS = 12;
const T4_RANKS = 22;
const ELITE_RANKS = 30;
// Costs per tier
const COST_T1 = 30;
const COST_T2 = 50;
const COST_T3 = 100;
const COST_T4 = 150;
const COST_ELITE = 500;
// ════════════════════════════════════════════════════════════════
//  RUNE_NODES — flat list of every node in the v2 tree
// ════════════════════════════════════════════════════════════════
export const RUNE_NODES = [
    // ════════════════════════════════════════════════════════════════
    //  WRATH BRANCH (offense — path: 'war')                  24 nodes
    // ════════════════════════════════════════════════════════════════
    // ── T1 entry stats (no gate) ──
    { id: 'wrath_whetstone', label: 'Whetstone',
        desc: 'Sharpen your weapons. Each rank adds flat attack damage to all heroes.',
        icon: '⚔', path: 'war', type: 'minor',
        x: spread(WRATH_X, 0, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        // Flat ATK halved 5 → 3 per user balance pass (rounded up from 2.5 so
        // the tooltip integer doesn't read as "more than half").
        effects: [{ stat: 'atk', perRank: 3 }] },
    { id: 'wrath_honed', label: 'Honed Steel',
        desc: 'A keen edge. Each rank adds critical strike chance.',
        icon: '◎', path: 'war', type: 'minor',
        x: spread(WRATH_X, 1, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'crit', perRank: 0.005 }] },
    { id: 'wrath_tempo', label: 'Battle Tempo',
        desc: 'Quicken your strikes. Each rank increases attack speed.',
        icon: '↯', path: 'war', type: 'minor',
        x: spread(WRATH_X, 2, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'atkSpd', perRank: 0.005 }] },
    { id: 'wrath_focus', label: 'Mana Focus',
        desc: 'Channel arcane energy. Each rank adds flat spell power.',
        icon: '✧', path: 'war', type: 'minor',
        x: spread(WRATH_X, 3, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        // Flat SP halved 7 → 4 per user balance pass (rounded up from 3.5).
        effects: [{ stat: 'sp', perRank: 4 }] },
    { id: 'wrath_surge', label: 'Spell Surge',
        desc: 'Empower your spells. Each rank boosts spell damage dealt.',
        icon: '✵', path: 'war', type: 'minor',
        x: spread(WRATH_X, 4, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'spellDmg', perRank: 0.008 }] },
    // ── T2 better stats (stage 5, 5 in branch) ──
    { id: 'wrath_tempered', label: 'Tempered Edge',
        desc: 'Forged in fire. Each rank improves critical strike damage.',
        icon: '✦', path: 'war', type: 'minor',
        x: spread(WRATH_X, 0, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['wrath_whetstone'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'critDmg', perRank: 0.014 }] },
    { id: 'wrath_fury', label: 'War Fury',
        desc: 'Channel battle rage. Each rank: +2% chance for an auto-attack to strike twice.',
        icon: '🜂', path: 'war', type: 'minor',
        x: spread(WRATH_X, 1, 5), y: T2_Y, maxRank: 3, costPerRank: COST_T2,
        requires: ['wrath_tempo'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'doubleAttackChance', perRank: 0.02 }] },
    { id: 'wrath_burning', label: 'Burning Steel',
        desc: 'Steel that hungers. Each rank adds heavy flat attack damage.',
        icon: '❥', path: 'war', type: 'minor',
        x: spread(WRATH_X, 2, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['wrath_whetstone'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        // Flat ATK halved 10 → 5 per user balance pass.
        effects: [{ stat: 'atk', perRank: 5 }] },
    { id: 'wrath_mastery', label: 'Arcane Mastery',
        desc: 'Deepen your knowledge of the arcane arts. Each rank adds spell power.',
        icon: '⚚', path: 'war', type: 'minor',
        x: spread(WRATH_X, 3, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['wrath_focus'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        // Flat SP halved 12 → 6 per user balance pass.
        effects: [{ stat: 'sp', perRank: 6 }] },
    { id: 'wrath_pen', label: 'Sundering Strikes',
        desc: 'Bypass armour. Each rank adds armour penetration to every hero.',
        icon: '▲', path: 'war', type: 'minor',
        x: spread(WRATH_X, 4, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['wrath_honed'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'armorPenPct', perRank: 0.01 }] },
    // ── T3 gameplay starts (stage 10, 12 in branch) ──
    { id: 'wrath_bloodthirst', label: 'Bloodthirst',
        desc: 'Killing renews you. Each rank: heroes heal 1% max HP per kill, +1% damage.',
        icon: '❤', path: 'war', type: 'notable',
        x: spread(WRATH_X, 0, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_tempered'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'killHealPct', perRank: 0.01 }, { stat: 'dmgPct', perRank: 0.01 }] },
    { id: 'wrath_berserker', label: 'Berserker',
        desc: 'Heroes below 30% HP deal +10% damage per rank.',
        icon: '⛧', path: 'war', type: 'notable',
        x: spread(WRATH_X, 1, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_fury'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'berserkLowHpPct', perRank: 0.10 }] },
    { id: 'wrath_lethality', label: 'Lethality',
        desc: 'Crits hit harder. Each rank adds base critical strike damage.',
        icon: '✖', path: 'war', type: 'notable',
        x: spread(WRATH_X, 2, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_tempered'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'critDmg', perRank: 0.02 }] },
    { id: 'wrath_slayer', label: 'Slayer',
        desc: 'Hunt the strong. Each rank adds +2% damage versus elites and bosses.',
        icon: '☠', path: 'war', type: 'notable',
        x: spread(WRATH_X, 3, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_burning'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'dmgVsElitePct', perRank: 0.02 }] },
    { id: 'wrath_cleave', label: 'Cleave',
        desc: 'Auto-attacks splash adjacent enemies. Each rank: +15% splash damage.',
        icon: '➤', path: 'war', type: 'notable',
        x: spread(WRATH_X, 4, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_burning'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'cleavePct', perRank: 0.15 }] },
    { id: 'wrath_spell_crit', label: "Sorcerer's Edge",
        desc: 'Spells can crit. Each rank adds +0.6% spell critical strike chance.',
        icon: '✨', path: 'war', type: 'notable',
        x: spread(WRATH_X, 5, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['wrath_mastery'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'spellCritChance', perRank: 0.006 }] },
    // ── T4 advanced (stage 20, 22 in branch) ──
    { id: 'wrath_headsman', label: 'Headsman',
        desc: 'Auto-attacks instantly slay non-boss enemies below 10% HP.',
        icon: '☠', path: 'war', type: 'elite',
        x: spread(WRATH_X, 0, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['wrath_slayer'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'headsmanFlag', perRank: 1 }] },
    { id: 'wrath_bloodfrenzy', label: 'Bloodfrenzy',
        desc: 'Each rank: kill grants +6% attack speed for 5s, stacking up to 5 times.',
        icon: '⚡', path: 'war', type: 'elite',
        x: spread(WRATH_X, 1, 7), y: T4_Y, maxRank: 3, costPerRank: COST_T4,
        requires: ['wrath_berserker'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'bloodfrenzyAtkSpd', perRank: 0.06 }] },
    { id: 'wrath_twin_fang', label: 'Twin Fang',
        desc: 'Every 5th critical hit strikes twice.',
        icon: '⚔', path: 'war', type: 'elite',
        x: spread(WRATH_X, 2, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['wrath_lethality'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'twinFangFlag', perRank: 1 }] },
    { id: 'wrath_lifesteal', label: 'Lifesteal',
        desc: 'Auto-attacks heal heroes. Each rank: +2% of damage dealt healed back.',
        icon: '🩸', path: 'war', type: 'elite',
        x: spread(WRATH_X, 3, 7), y: T4_Y, maxRank: 3, costPerRank: COST_T4,
        requires: ['wrath_bloodthirst'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'lifestealPct', perRank: 0.02 }] },
    { id: 'wrath_spellbound', label: 'Spellbound',
        desc: 'Spell crits hit harder. Each rank: +5% spell critical damage.',
        icon: '⚝', path: 'war', type: 'elite',
        x: spread(WRATH_X, 4, 7), y: T4_Y, maxRank: 3, costPerRank: COST_T4,
        requires: ['wrath_spell_crit'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'spellCritDmg', perRank: 0.05 }] },
    { id: 'wrath_brutality', label: 'Brutality',
        desc: 'Each rank: +0.4% damage per stage cleared this run (caps at +12%).',
        icon: '✺', path: 'war', type: 'elite',
        x: spread(WRATH_X, 5, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['wrath_cleave'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'dmgPerStagePct', perRank: 0.004 }] },
    { id: 'wrath_crimson_wound', label: 'Crimson Wound',
        desc: 'Critical strikes apply a bleed: 2% of max HP over 4 seconds.',
        icon: '🩸', path: 'war', type: 'elite',
        x: spread(WRATH_X, 6, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['wrath_lethality'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'crimsonWoundFlag', perRank: 1 }] },
    // ── Elite (stage 30, 30 in branch) ──
    { id: 'wrath_warmonger', label: 'Warmonger',
        desc: 'Heroes deal +15% damage per fallen ally (cap +60%). The blood-pact endgame.',
        icon: 'ᚹ', path: 'war', type: 'keystone',
        // Aligned vertically with its prereq (Spellbound, idx 4 of 7 → x=380)
        // per user feedback so the keystone sits directly under the node it
        // depends on instead of floating at the branch column centre.
        x: spread(WRATH_X, 4, 7), y: ELITE_Y, maxRank: 1, costPerRank: COST_ELITE,
        requires: ['wrath_warmonger_gate'], requiresZone: 50, requiresBranchRanks: ELITE_RANKS,
        effects: [{ stat: 'warmongerPctPerDeadAlly', perRank: 0.15 }] },
    // ════════════════════════════════════════════════════════════════
    //  WARD BRANCH (defense — path: 'defense')              24 nodes
    // ════════════════════════════════════════════════════════════════
    // ── T1 ──
    { id: 'ward_vitality', label: 'Vitality',
        desc: 'A stronger heart. Each rank adds +3% max HP to all heroes.',
        icon: '❤', path: 'defense', type: 'minor',
        x: spread(WARD_X, 0, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        // Vitality: nerfed 0.05 → 0.03 per rank as part of the defense-tree
        // rebalance pass. Combined with the moves on Fortitude / Hardened
        // Bones / Unyielding the total HP-from-tree drops from +38 % to
        // +23 %, leaving room for the new melee class HP/DEF buffs without
        // double-stacking durability.
        effects: [{ stat: 'hpPctBonus', perRank: 0.03 }] },
    { id: 'ward_thickskin', label: 'Thick Skin',
        desc: 'Tougher hide. Each rank adds flat defense to every hero.',
        icon: '⛨', path: 'defense', type: 'minor',
        x: spread(WARD_X, 1, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'defFlat', perRank: 3 }] },
    { id: 'ward_recovery', label: 'Slow Recovery',
        desc: 'Wounds knit. Each rank: +0.2% max HP regenerated per second.',
        icon: '✚', path: 'defense', type: 'minor',
        x: spread(WARD_X, 2, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'hpRegenPctPerSec', perRank: 0.002 }] },
    { id: 'ward_bracing', label: 'Bracing',
        desc: 'Soak the blow. Each rank reduces damage received by 1%.',
        icon: '⚔', path: 'defense', type: 'minor',
        x: spread(WARD_X, 3, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'drPct', perRank: 0.01 }] },
    { id: 'ward_warding', label: 'Warding',
        desc: 'Old runes guard the body. Each rank adds 0.5% resistance to all elements.',
        icon: '⛨', path: 'defense', type: 'minor',
        x: spread(WARD_X, 4, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'allRes', perRank: 0.005 }] },
    // ── T2 ──
    { id: 'ward_fortitude', label: 'Fortitude',
        desc: 'Deeper wells of life. Each rank adds +1% max HP.',
        icon: '❤', path: 'defense', type: 'minor',
        x: spread(WARD_X, 0, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['ward_vitality'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        // Nerfed 0.02 → 0.01 per rank in the defense-tree rebalance pass.
        effects: [{ stat: 'hpPctBonus', perRank: 0.01 }] },
    { id: 'ward_aegis', label: 'Aegis',
        desc: 'Magical shields hold longer. Each rank: +5% effectiveness on all hero shields.',
        icon: '⛨', path: 'defense', type: 'minor',
        x: spread(WARD_X, 1, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['ward_thickskin'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'shieldEffPct', perRank: 0.05 }] },
    { id: 'ward_hardened', label: 'Hardened Bones',
        desc: 'Iron-bound. Each rank adds +4 flat defense.',
        icon: '⛨', path: 'defense', type: 'minor',
        x: spread(WARD_X, 2, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['ward_thickskin'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        // Nerfed 5 → 4 per rank in the defense-tree rebalance pass.
        effects: [{ stat: 'defFlat', perRank: 4 }] },
    { id: 'ward_steady', label: 'Steady Heart',
        desc: 'Open vessel. Each rank: +1% healing received from every source.',
        icon: '✚', path: 'defense', type: 'minor',
        x: spread(WARD_X, 3, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['ward_recovery'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'healingReceivedPct', perRank: 0.01 }] },
    { id: 'ward_ironhide', label: 'Ironhide',
        desc: 'Damage glances off bosses. Each rank: +0.5% damage reduction against boss attacks.',
        icon: '⛨', path: 'defense', type: 'minor',
        x: spread(WARD_X, 4, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['ward_bracing'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'bossDrPct', perRank: 0.005 }] },
    // ── T3 ──
    { id: 'ward_last_vow', label: 'Last Vow',
        desc: 'Heroes below 30% HP receive 5% less damage per rank.',
        icon: '⛨', path: 'defense', type: 'notable',
        x: spread(WARD_X, 0, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_fortitude'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'lastVowDrPct', perRank: 0.05 }] },
    { id: 'ward_thornwall', label: 'Thornwall',
        desc: 'Reflect 5% of melee damage received back to the attacker per rank.',
        icon: '✺', path: 'defense', type: 'notable',
        x: spread(WARD_X, 1, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_aegis'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'thornsPct', perRank: 0.05 }] },
    { id: 'ward_resolute', label: 'Resolute',
        desc: 'Resist crowd control. Each rank: +5% chance to ignore stun, slow and silence.',
        icon: '⚓', path: 'defense', type: 'notable',
        x: spread(WARD_X, 2, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_hardened'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'ccResistPct', perRank: 0.05 }] },
    { id: 'ward_unyielding', label: 'Unyielding',
        desc: 'Each rank: +4 defense and +1% max HP.',
        icon: '⛰', path: 'defense', type: 'notable',
        x: spread(WARD_X, 3, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_hardened'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        // Nerfed defense 6 → 4 per rank in the defense-tree rebalance pass
        // (HP +1 % per rank kept). Total tree DEF max drops from +58 to +47.
        effects: [{ stat: 'defFlat', perRank: 4 }, { stat: 'hpPctBonus', perRank: 0.01 }] },
    { id: 'ward_purify', label: 'Purify',
        desc: 'Damage-over-time effects on heroes deal 20% less damage per rank.',
        icon: '✦', path: 'defense', type: 'notable',
        x: spread(WARD_X, 4, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_steady'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'dotResistPct', perRank: 0.20 }] },
    { id: 'ward_evasion', label: 'Evasion',
        desc: 'Each rank: +0.5% chance to dodge any attack outright.',
        icon: '◌', path: 'defense', type: 'notable',
        x: spread(WARD_X, 5, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['ward_ironhide'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'dodgePct', perRank: 0.005 }] },
    // ── T4 ──
    { id: 'ward_last_breath', label: 'Last Breath',
        desc: 'Once per run, a hero who would die instead survives at 30% HP.',
        icon: '✚', path: 'defense', type: 'elite',
        x: spread(WARD_X, 0, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['ward_last_vow'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'lastBreathFlag', perRank: 1 }] },
    { id: 'ward_vow_of_iron', label: 'Vow of Iron',
        desc: 'Front-row heroes share 10% of damage received with vanguard slots.',
        icon: '⛨', path: 'defense', type: 'elite',
        x: spread(WARD_X, 1, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['ward_unyielding'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'vowOfIronFlag', perRank: 1 }] },
    { id: 'ward_revenant', label: 'Revenant',
        desc: 'The first hero to fall each run resurrects with full HP.',
        icon: 'ᛞ', path: 'defense', type: 'elite',
        x: spread(WARD_X, 2, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['ward_purify'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'revenantFlag', perRank: 1 }] },
    { id: 'ward_stalwart_stance', label: 'Stalwart Stance',
        desc: 'Each rank: front-row heroes gain +15% defense.',
        icon: '⛰', path: 'defense', type: 'elite',
        x: spread(WARD_X, 3, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['ward_unyielding'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'frontDefPct', perRank: 0.15 }] },
    { id: 'ward_eternal_hearth', label: 'Eternal Hearth',
        desc: 'Each rank: −5% damage received from area-of-effect attacks.',
        icon: '✚', path: 'defense', type: 'elite',
        x: spread(WARD_X, 4, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['ward_last_vow'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'aoeDamageReductionPct', perRank: 0.05 }] },
    { id: 'ward_wardmaster', label: 'Wardmaster',
        desc: 'Each rank: +0.6% resistance to all damage types.',
        icon: '⛨', path: 'defense', type: 'elite',
        x: spread(WARD_X, 5, 7), y: T4_Y, maxRank: 3, costPerRank: COST_T4,
        requires: ['ward_thornwall'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'allRes', perRank: 0.006 }] },
    { id: 'ward_hearthlight', label: 'Hearthlight',
        desc: 'Each rank: heroes heal 5% max HP at the start of every wave.',
        icon: '✺', path: 'defense', type: 'elite',
        x: spread(WARD_X, 6, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['ward_evasion'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'healPerWavePct', perRank: 0.05 }] },
    // ── Elite ──
    { id: 'ward_eternal_bastion', label: 'Eternal Bastion',
        desc: 'All heroes share a single buffer pool equal to 25% of party total max HP. Refilled at zone clear.',
        icon: '⛨', path: 'defense', type: 'keystone',
        // Aligned with its prereq (Vow of Iron, idx 1 of 7 → x=840) per user feedback.
        x: spread(WARD_X, 1, 7), y: ELITE_Y, maxRank: 1, costPerRank: COST_ELITE,
        requires: ['ward_eternal_bastion_gate'], requiresZone: 50, requiresBranchRanks: ELITE_RANKS,
        effects: [{ stat: 'eternalBastionFlag', perRank: 1 }] },
    // ════════════════════════════════════════════════════════════════
    //  CUNNING BRANCH (utility — path: 'fortune')           24 nodes
    // ════════════════════════════════════════════════════════════════
    // ── T1 ──
    { id: 'cun_fortune', label: 'Fortune',
        desc: 'Each rank: +1.2% gold gained from all sources.',
        icon: '⦿', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 0, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'goldPct', perRank: 0.012 }] },
    { id: 'cun_scholar_eye', label: "Scholar's Eye",
        desc: 'Each rank: +1.5% experience gained from all sources.',
        icon: '⚜', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 1, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'xpPct', perRank: 0.015 }] },
    { id: 'cun_swift_mind', label: 'Swift Mind',
        desc: 'Each rank: -1% all ability cooldowns.',
        icon: '↻', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 2, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'cdr', perRank: 0.01 }] },
    { id: 'cun_scavenger', label: 'Scavenger',
        desc: 'Each rank: +0.5% loot drop chance.',
        icon: '◉', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 3, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'lootPct', perRank: 0.005 }] },
    { id: 'cun_recycler', label: 'Recycler',
        desc: 'Each rank: +5% chance to double the rune refund when recycling a sigil.',
        icon: '♻', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 4, 5), y: T1_Y, maxRank: 5, costPerRank: COST_T1, requires: [],
        effects: [{ stat: 'chanceToDoubleRecycle', perRank: 0.05 }] },
    // ── T2 ──
    { id: 'cun_prosperity', label: 'Prosperity',
        desc: 'Each rank: +0.5% gold gained, +0.5% loot drop chance.',
        icon: '✯', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 0, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['cun_fortune'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'goldPct', perRank: 0.005 }, { stat: 'lootPct', perRank: 0.005 }] },
    { id: 'cun_swift_steps', label: 'Swift Steps',
        desc: 'Each rank: +0.2% chance for an ability cast to skip its cooldown entirely.',
        icon: '↻', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 1, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['cun_swift_mind'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'spellNoCdChance', perRank: 0.002 }] },
    { id: 'cun_quartermaster', label: 'Quartermaster',
        desc: 'Each rank: +1 vendor lock slot. Locks the merchant\'s best wares between refreshes.',
        icon: '⚿', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 2, 5), y: T2_Y, maxRank: 2, costPerRank: COST_T2,
        requires: ['cun_fortune'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'vendorLocks', perRank: 1 }] },
    { id: 'cun_inquisitor', label: 'Inquisitor',
        desc: 'Each rank: +6% experience from all sources.',
        icon: '⚖', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 3, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['cun_scholar_eye'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'xpPct', perRank: 0.06 }] },
    { id: 'cun_runeforge', label: 'Runeforge',
        desc: 'Each rank: +1% rune token drop chance.',
        icon: '◆', path: 'fortune', type: 'minor',
        x: spread(CUNNING_X, 4, 5), y: T2_Y, maxRank: 5, costPerRank: COST_T2,
        requires: ['cun_scavenger'], requiresZone: T2_STAGE, requiresBranchRanks: T2_RANKS,
        effects: [{ stat: 'runeDropPct', perRank: 0.01 }] },
    // ── T3 ──
    { id: 'cun_rune_stride', label: 'Rune Stride',
        desc: '+1 maximum hero swap move per wave (stacks with Wind Stride for +2 total).',
        icon: '➤', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 0, 6), y: T3_Y, maxRank: 1, costPerRank: COST_T3,
        requires: ['cun_prosperity'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'bonusMovesPerWave', perRank: 1 }] },
    { id: 'cun_banished_tome', label: 'Banished Tome',
        desc: '+1 free pre-run book ban slot (no rune cost).',
        icon: '📖', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 1, 6), y: T3_Y, maxRank: 1, costPerRank: COST_T3,
        requires: ['cun_inquisitor'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'extraBookBan', perRank: 1 }] },
    { id: 'cun_tome_insight', label: 'Tome Insight',
        desc: 'Each rank: +1 free reroll PER RUN on book choices (base 1 + N).',
        icon: '⚜', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 2, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['cun_inquisitor'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'bookRerolls', perRank: 1 }] },
    { id: 'cun_foresight', label: 'Foresight',
        desc: 'Each rank: +1 free reroll on every talent choice popup.',
        icon: '✦', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 3, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['cun_swift_steps'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'talentRerolls', perRank: 1 }] },
    { id: 'cun_pilgrims_tithe', label: "Pilgrim's Tithe",
        desc: 'Each rank: -10% gold cost on Gilded Altar purchases AND on the Rune Stone / Eternal Rune Stone checkpoint setup (Stage 10 / 30).',
        icon: '⛪', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 4, 6), y: T3_Y, maxRank: 3, costPerRank: COST_T3,
        requires: ['cun_quartermaster'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'shrineDiscountPct', perRank: 0.10 }] },
    { id: 'cun_boon_hoarder', label: 'Boon Hoarder',
        desc: '+1 active boon slot (carry one extra boon at a time).',
        icon: '✺', path: 'fortune', type: 'notable',
        x: spread(CUNNING_X, 5, 6), y: T3_Y, maxRank: 1, costPerRank: COST_T3,
        requires: ['cun_runeforge'], requiresZone: T3_STAGE, requiresBranchRanks: T3_RANKS,
        effects: [{ stat: 'extraBoonSlot', perRank: 1 }] },
    // ── T4 ──
    { id: 'cun_wind_stride', label: 'Wind Stride',
        desc: '+1 maximum hero swap move per wave (stacks with Rune Stride for +2 total).',
        icon: '🜁', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 0, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['cun_rune_stride'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'bonusMovesPerWave', perRank: 1 }] },
    { id: 'cun_master_alchemist', label: 'Master Alchemist',
        desc: 'Each rank: +1 consumable slot on the potion bar.',
        icon: '⚗', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 1, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['cun_tome_insight'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'extra_potion_slots', perRank: 1 }] },
    { id: 'cun_sigil_bond', label: 'Sigil Bond',
        desc: 'Each rank: +1 sigil equip slot.',
        icon: '◆', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 2, 7), y: T4_Y, maxRank: 2, costPerRank: COST_T4,
        requires: ['cun_runeforge'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'sigil_slots', perRank: 1 }] },
    { id: 'cun_eternal_rune', label: 'Eternal Rune Stone',
        desc: 'Unlock a second checkpoint that anchors your run at Stage 30 (in addition to Stage 10).',
        icon: 'ᚱ', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 3, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['cun_pilgrims_tithe'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'deepCheckpoint', perRank: 1 }] },
    { id: 'cun_premonition', label: 'Premonition',
        desc: 'Preview the next wave\'s enemy mix on the zone-cleared screen.',
        icon: '◉', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 4, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['cun_foresight'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'premonitionFlag', perRank: 1 }] },
    { id: 'cun_pathseer', label: 'Pathseer',
        desc: 'Auto-Run also picks Shrine of Boons and Gilded Altar choices for you.',
        icon: '✦', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 5, 7), y: T4_Y, maxRank: 1, costPerRank: COST_T4,
        requires: ['cun_pilgrims_tithe'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'autoRunPlusFlag', perRank: 1 }] },
    { id: 'cun_scholar_pact', label: 'Scholar Pact',
        desc: 'Each rank: every 5 stages cleared grants +1 extra book choice.',
        icon: '⚜', path: 'fortune', type: 'elite',
        x: spread(CUNNING_X, 6, 7), y: T4_Y, maxRank: 3, costPerRank: COST_T4,
        requires: ['cun_tome_insight'], requiresZone: T4_STAGE, requiresBranchRanks: T4_RANKS,
        effects: [{ stat: 'bookChoiceBonus', perRank: 1 }] },
    // ── Elite ──
    { id: 'cun_warlords_crown', label: 'Tribute',
        desc: 'Every rune drop is doubled. Mobs, elites, bosses — every source. The pact of the rune-king.',
        icon: '✦', path: 'fortune', type: 'keystone',
        // Aligned with its prereq (Sigil Bond, idx 2 of 7 → x=1480) per user feedback.
        x: spread(CUNNING_X, 2, 7), y: ELITE_Y, maxRank: 1, costPerRank: COST_ELITE,
        requires: ['cun_warlords_crown_gate'], requiresZone: 50, requiresBranchRanks: ELITE_RANKS,
        effects: [{ stat: 'tributeFlag', perRank: 1 }] },
    // ════════════════════════════════════════════════════════════════
    //  INVOCATIONS — one per branch, sits to the RIGHT of the elite
    //  keystone (Warmonger / Eternal Bastion / Tribute), at the SAME
    //  y = ELITE_Y so the elite stays centered in its branch column and
    //  the invocation reads as a small companion node right next to it.
    //  Per user feedback this layout replaced the previous "tier below
    //  the elite" design which was pushing the elite keystones off-centre.
    //
    //  Gating: stage 40 unlock + 30 ranks in the matching branch (per user
    //  feedback — bumped 15 → 30 so invocations are a real branch
    //  commitment, not a half-investment). Cost 1000 runes. Each grants a
    //  flag on PassiveBonuses; the castable ability is wired in BattleScene
    //  against the flag.
    // ════════════════════════════════════════════════════════════════
    { id: 'inv_avenging_wrath', label: 'Avenging Wrath',
        desc: 'Invocation: every hero deals +50% damage for 8s and recovers 25% rage / mana / energy. 75s cooldown.',
        icon: '⚔', path: 'war', type: 'invocation',
        x: WRATH_X + 95, y: ELITE_Y, maxRank: 1, costPerRank: 1000,
        requires: [], requiresZone: 40, requiresBranchRanks: 30,
        effects: [{ stat: 'avengingWrathFlag', perRank: 1 }] },
    { id: 'inv_aegis_of_light', label: 'Aegis of Light',
        desc: 'Invocation: heal every hero for 30% max HP and grant a 5s 50% damage-reduction halo. 60s cooldown.',
        icon: '☀', path: 'defense', type: 'invocation',
        x: WARD_X + 95, y: ELITE_Y, maxRank: 1, costPerRank: 1000,
        requires: [], requiresZone: 40, requiresBranchRanks: 30,
        effects: [{ stat: 'aegisOfLightFlag', perRank: 1 }] },
    { id: 'inv_time_pull', label: 'Time Pull',
        desc: 'Invocation: rewind every hero ability cooldown to zero. Once per zone, 90s cooldown.',
        icon: '⧗', path: 'fortune', type: 'invocation',
        x: CUNNING_X + 95, y: ELITE_Y, maxRank: 1, costPerRank: 1000,
        requires: [], requiresZone: 40, requiresBranchRanks: 30,
        effects: [{ stat: 'timePullFlag', perRank: 1 }] },
];
// Authoritative `requires` map (user-curated). Every entry: the listed node
// requires the named prerequisite (must have ≥ 1 rank in the prereq before
// the listed node unlocks). All other nodes get `requires = []` cleared
// below. Tier gating (requiresBranchRanks + requiresZone) still applies on
// top of this — a node like Warmonger still needs the elite branch-rank
// budget AND its prereq.
const NODE_REQUIRES = {
    // ── WRATH ──────────────────────────────────────────────────────────
    wrath_lethality: 'wrath_berserker', // Lethality ← Berserker
    wrath_spell_crit: 'wrath_cleave', // Sorcerer's Edge ← Cleave
    wrath_bloodthirst: 'wrath_mastery', // Bloodthirst ← Arcane Mastery
    wrath_slayer: 'wrath_bloodthirst', // Slayer ← Bloodthirst
    wrath_warmonger: 'wrath_spellbound', // Warmonger ← Spellbound
    // ── WARD ───────────────────────────────────────────────────────────
    ward_eternal_bastion: 'ward_vow_of_iron', // Eternal Bastion ← Vow of Iron
    ward_thornwall: 'ward_last_vow', // Thornwall ← Last Vow
    ward_last_vow: 'ward_hardened', // Last Vow ← Hardened Bones
    ward_evasion: 'ward_aegis', // Evasion ← Aegis
    ward_unyielding: 'ward_purify', // Unyielding ← Purify
    ward_steady: 'ward_warding', // Steady Heart ← Warding
    // ── CUNNING ────────────────────────────────────────────────────────
    cun_warlords_crown: 'cun_sigil_bond', // Tribute ← Sigil Bond
    cun_eternal_rune: 'cun_pilgrims_tithe', // Eternal Rune Stone ← Pilgrim's Tithe
    cun_pilgrims_tithe: 'cun_banished_tome', // Pilgrim's Tithe ← Banished Tome
    cun_banished_tome: 'cun_inquisitor', // Banished Tome ← Inquisitor
    cun_rune_stride: 'cun_boon_hoarder', // Rune Stride ← Boon Hoarder
    cun_wind_stride: 'cun_premonition', // Wind Stride ← Premonition
};
for (const n of RUNE_NODES) {
    const prereq = NODE_REQUIRES[n.id];
    n.requires = prereq ? [prereq] : [];
}
/** Total height of the scroll canvas. Invocations now sit at the same y as
 *  the elite keystone (to the right of it), so the tree no longer needs the
 *  extra 160 px below ELITE_Y. */
export const RUNE_TREE_HEIGHT = ELITE_Y + 200;
/** Total cost in runes to fully fill every node at every rank. */
export const TREE_TOTAL_COST = RUNE_NODES.reduce((s, n) => s + n.maxRank * n.costPerRank, 0);
/** Maximum runes that may ever be spent in the tree (matches MAX_TREE_POINTS budget).
 *  Kept for legacy callers; the v2 cap is enforced by `MAX_TREE_POINTS` (rank count). */
export const TREE_MAX_SPEND = TREE_TOTAL_COST;
// ── Helper lookups ──
const NODE_MAP = new Map(RUNE_NODES.map((n) => [n.id, n]));
export function getRuneNode(id) { return NODE_MAP.get(id); }
/** Sum every rank held in the given branch (path). */
export function getBranchRanksSpent(tree, path) {
    let total = 0;
    for (const node of RUNE_NODES) {
        if (node.path !== path)
            continue;
        total += tree[node.id] ?? 0;
    }
    return total;
}
/** Sum every rank held across the entire tree. Used by the budget gate. */
export function getTotalRanksSpent(tree) {
    let total = 0;
    for (const node of RUNE_NODES) {
        total += tree[node.id] ?? 0;
    }
    return total;
}
/** Get current rank of a node (0 = not unlocked). */
export function getNodeRank(tree, id) {
    return tree[id] ?? 0;
}
/** True if all requirements for a node are met. */
export function nodeReqsMet(tree, node, ctx) {
    // Parent node requirements (must be at full rank)
    for (const reqId of node.requires) {
        const reqNode = NODE_MAP.get(reqId);
        if (!reqNode)
            return false;
        if (getNodeRank(tree, reqId) < reqNode.maxRank)
            return false;
    }
    // Zone gate
    if (node.requiresZone !== undefined) {
        const highest = ctx?.highestLevel ?? 0;
        if (highest < node.requiresZone)
            return false;
    }
    // Achievement gate
    if (node.requiresAchievement !== undefined) {
        const ids = ctx?.completedAchievementIds ?? [];
        if (!ids.includes(node.requiresAchievement))
            return false;
    }
    // Branch-rank gate (v2 tier system)
    if (node.requiresBranchRanks !== undefined && node.requiresBranchRanks > 0) {
        if (getBranchRanksSpent(tree, node.path) < node.requiresBranchRanks)
            return false;
    }
    return true;
}
/** True if the node can be upgraded — reqs met, not at max, enough runes,
 *  and player hasn't hit the global rank budget. */
export function canUpgradeNode(tree, node, tokens, ctx) {
    const rank = getNodeRank(tree, node.id);
    if (rank >= node.maxRank)
        return false;
    if (!nodeReqsMet(tree, node, ctx))
        return false;
    if (tokens < node.costPerRank)
        return false;
    if (getTotalRanksSpent(tree) >= MAX_TREE_POINTS)
        return false;
    return true;
}
/** Total tokens (runes) spent in the tree at current ranks. */
export function totalSpentTokens(tree) {
    let total = 0;
    for (const node of RUNE_NODES) {
        total += (tree[node.id] ?? 0) * node.costPerRank;
    }
    return total;
}
// ════════════════════════════════════════════════════════════════
//  LEGACY MIGRATION — refund every rank from the v1 tree
// ════════════════════════════════════════════════════════════════
//
// Players who invested under the old (war/arcane/fortune/defense + linked +
// unique + utility) tree get every rune back. Map keys are the v1 node IDs;
// values are the v1 cost-per-rank. Multiplied by the saved rank to compute
// the per-node refund.
export const LEGACY_NODE_COSTS = {
    // War path
    war_whetstone: 25, war_fury: 28, war_swift: 28, war_tempered: 28, war_executioner: 35,
    war_colossus: 80, war_warlord: 90, war_slaughter: 100, war_bloodrage: 110,
    war_deathblow: 250, war_warchief: 300, war_berserk: 1000,
    // Arcane path
    arc_conduit: 25, arc_resonance: 25, arc_cdr: 28, arc_mastery: 35, arc_amplify: 35,
    arc_attunement: 80, arc_surge: 90, arc_vortex: 100, arc_echo: 110,
    arc_singularity: 250, arc_tempest: 300, arc_divine: 1000,
    // Fortune path
    for_treasure: 25, for_prospector: 25, for_scavenger: 25, for_lucky: 35, for_broker: 28,
    for_hoarder: 90, for_fence: 80, for_mastery: 110, for_tithe: 100,
    for_wealth: 300, for_kingpin: 250, for_pact: 1000,
    // Cross-path bridges
    cross_war_arc: 400, cross_arc_for: 400, cross_war_for: 350,
    cross_arc_war: 300, cross_for_arc: 280,
    // Defense cluster
    def_fire: 50, def_arcane: 50, def_frost: 50, def_shadow: 50, def_bulwark: 300,
    // Eternal rune stone (linked)
    rune_deep_checkpoint: 500,
    // Unique standalone nodes
    uniq_glass_cannon: 150, uniq_hoarder: 120, uniq_scholar: 200, uniq_lucky_draw: 180,
    // Utility (potion + sigil slots)
    util_potion_slot_3: 150, util_potion_slot_4: 300,
    util_sigil_slot_2: 200, util_sigil_slot_3: 400,
};
/** Total rune refund for a v1 tree state. */
export function computeLegacyRefund(legacyTree) {
    let refund = 0;
    for (const [id, rank] of Object.entries(legacyTree)) {
        const cost = LEGACY_NODE_COSTS[id];
        if (cost === undefined)
            continue; // unknown node — silently drop
        refund += cost * rank;
    }
    return refund;
}
/** Migrate a v1 tree to v2: returns the rune refund. The caller is responsible
 *  for adding `refund` to the player's runes and clearing the tree. */
export function migrateLegacyTree(legacyTree) {
    return { refund: computeLegacyRefund(legacyTree), cleared: {} };
}
export function getPassiveBonuses(tree) {
    const b = {
        atk: 0, atkSpd: 0, crit: 0, critDmg: 0, dmgPct: 0,
        mp: 0, cdr: 0, sp: 0, spellDmg: 0,
        goldPct: 0, lootPct: 0, resourcePct: 0, vendorLocks: 0,
        fireRes: 0, frostRes: 0, shadowRes: 0, arcaneRes: 0, allRes: 0,
        keystoneBerserk: false, keystoneDivineWrath: false, keystoneAncientPact: false,
        deepCheckpoint: false,
        extraPotionSlots: 0, sigilSlots: 0,
        hpPenalty: 0, vendorRefreshSpeed: 0, bookChoiceBonus: 0, rareLootUpgrade: 0,
        // WRATH v2
        armorPenPct: 0, spellCritChance: 0, spellCritDmg: 0, dmgVsElitePct: 0,
        cleavePct: 0, lifestealPct: 0, dmgPerStagePct: 0, berserkLowHpPct: 0,
        killHealPct: 0, warmongerPctPerDeadAlly: 0, bloodfrenzyAtkSpd: 0, doubleAttackChance: 0,
        headsmanFlag: false, twinFangFlag: false, crimsonWoundFlag: false,
        // WARD v2
        hpFlat: 0, hpPctBonus: 0, defFlat: 0, hpRegenPerSec: 0,
        hpRegenPctPerSec: 0, healingReceivedPct: 0, bossDrPct: 0, aoeDamageReductionPct: 0,
        drPct: 0, shieldEffPct: 0, ccResistPct: 0, dotResistPct: 0,
        dodgePct: 0, thornsPct: 0, frontDefPct: 0, extraRespawns: 0,
        healPerWavePct: 0, lastVowDrPct: 0,
        lastBreathFlag: false, vowOfIronFlag: false, revenantFlag: false, eternalBastionFlag: false,
        // CUNNING v2
        xpPct: 0, recyclingPct: 0, chanceToDoubleRecycle: 0, spellNoCdChance: 0, runeDropPct: 0,
        bonusMovesPerWave: 0, extraBookBan: 0, bookRerolls: 0, talentRerolls: 0,
        shrineDiscountPct: 0, extraBoonSlot: 0, invSlotBonus: 0,
        premonitionFlag: false, autoRunPlusFlag: false, foresightExtraWaves: 0,
        tributeFlag: false,
        avengingWrathFlag: false, aegisOfLightFlag: false, timePullFlag: false,
    };
    // Stats that are NOT scaled by the global passive multiplier (booleans, integer counts)
    const UNSCALED = new Set([
        'vendorRefreshSpeed', 'bookChoiceBonus', 'extra_potion_slots', 'sigil_slots',
        // v2 unscaled (counts / flags)
        'bonusMovesPerWave', 'extraBookBan', 'bookRerolls', 'talentRerolls',
        'extraBoonSlot', 'invSlotBonus', 'extraRespawns', 'foresightExtraWaves',
    ]);
    // Soft scale — kept at 1.0 in v2 since costs and per-rank values are tuned for the new budget.
    const PASSIVE_STAT_SCALE = 1.0;
    for (const node of RUNE_NODES) {
        const rank = tree[node.id] ?? 0;
        if (rank === 0)
            continue;
        for (const eff of node.effects) {
            const isBoolStat = eff.stat.endsWith('Flag') || eff.stat.startsWith('keystone_') || eff.stat === 'deepCheckpoint';
            const skipScale = isBoolStat || UNSCALED.has(eff.stat);
            const v = skipScale ? eff.perRank * rank : eff.perRank * rank * PASSIVE_STAT_SCALE;
            switch (eff.stat) {
                // ── existing offensive ──
                case 'atk':
                    b.atk += v;
                    break;
                case 'atkSpd':
                    b.atkSpd += v;
                    break;
                case 'crit':
                    b.crit += v;
                    break;
                case 'critDmg':
                    b.critDmg += v;
                    break;
                case 'dmgPct':
                    b.dmgPct += v;
                    break;
                case 'mp':
                    b.mp += v;
                    break;
                case 'cdr':
                    b.cdr += v;
                    break;
                case 'sp':
                    b.sp += v;
                    break;
                case 'spellDmg':
                    b.spellDmg += v;
                    break;
                case 'goldPct':
                    b.goldPct += v;
                    break;
                case 'lootPct':
                    b.lootPct += v;
                    break;
                case 'resourcePct':
                    b.resourcePct += v;
                    break;
                case 'vendorLocks':
                    b.vendorLocks += v;
                    break;
                case 'fireRes':
                    b.fireRes += v;
                    break;
                case 'frostRes':
                    b.frostRes += v;
                    break;
                case 'shadowRes':
                    b.shadowRes += v;
                    break;
                case 'arcaneRes':
                    b.arcaneRes += v;
                    break;
                case 'allRes':
                    b.allRes += v;
                    break;
                case 'keystone_berserk':
                    if (rank >= 1)
                        b.keystoneBerserk = true;
                    break;
                case 'keystone_divine_wrath':
                    if (rank >= 1)
                        b.keystoneDivineWrath = true;
                    break;
                case 'keystone_ancient_pact':
                    if (rank >= 1)
                        b.keystoneAncientPact = true;
                    break;
                case 'deepCheckpoint':
                    if (rank >= 1)
                        b.deepCheckpoint = true;
                    break;
                case 'extra_potion_slots':
                    b.extraPotionSlots += v;
                    break;
                case 'sigil_slots':
                    b.sigilSlots += v;
                    break;
                case 'hpPenalty':
                    b.hpPenalty += v;
                    break;
                case 'vendorRefreshSpeed':
                    b.vendorRefreshSpeed += v;
                    break;
                case 'bookChoiceBonus':
                    b.bookChoiceBonus += v;
                    break;
                case 'rareLootUpgrade':
                    b.rareLootUpgrade += v;
                    break;
                // ── WRATH v2 ──
                case 'armorPenPct':
                    b.armorPenPct += v;
                    break;
                case 'spellCritChance':
                    b.spellCritChance += v;
                    break;
                case 'spellCritDmg':
                    b.spellCritDmg += v;
                    break;
                case 'dmgVsElitePct':
                    b.dmgVsElitePct += v;
                    break;
                case 'cleavePct':
                    b.cleavePct += v;
                    break;
                case 'lifestealPct':
                    b.lifestealPct += v;
                    break;
                case 'dmgPerStagePct':
                    b.dmgPerStagePct += v;
                    break;
                case 'berserkLowHpPct':
                    b.berserkLowHpPct += v;
                    break;
                case 'killHealPct':
                    b.killHealPct += v;
                    break;
                case 'warmongerPctPerDeadAlly':
                    b.warmongerPctPerDeadAlly += v;
                    break;
                case 'bloodfrenzyAtkSpd':
                    b.bloodfrenzyAtkSpd += v;
                    break;
                case 'doubleAttackChance':
                    b.doubleAttackChance += v;
                    break;
                case 'headsmanFlag':
                    if (rank >= 1)
                        b.headsmanFlag = true;
                    break;
                case 'twinFangFlag':
                    if (rank >= 1)
                        b.twinFangFlag = true;
                    break;
                case 'crimsonWoundFlag':
                    if (rank >= 1)
                        b.crimsonWoundFlag = true;
                    break;
                // ── WARD v2 ──
                case 'hpFlat':
                    b.hpFlat += v;
                    break;
                case 'hpPctBonus':
                    b.hpPctBonus += v;
                    break;
                case 'defFlat':
                    b.defFlat += v;
                    break;
                case 'hpRegenPerSec':
                    b.hpRegenPerSec += v;
                    break;
                case 'hpRegenPctPerSec':
                    b.hpRegenPctPerSec += v;
                    break;
                case 'healingReceivedPct':
                    b.healingReceivedPct += v;
                    break;
                case 'bossDrPct':
                    b.bossDrPct += v;
                    break;
                case 'aoeDamageReductionPct':
                    b.aoeDamageReductionPct += v;
                    break;
                case 'drPct':
                    b.drPct += v;
                    break;
                case 'shieldEffPct':
                    b.shieldEffPct += v;
                    break;
                case 'ccResistPct':
                    b.ccResistPct += v;
                    break;
                case 'dotResistPct':
                    b.dotResistPct += v;
                    break;
                case 'dodgePct':
                    b.dodgePct += v;
                    break;
                case 'thornsPct':
                    b.thornsPct += v;
                    break;
                case 'frontDefPct':
                    b.frontDefPct += v;
                    break;
                case 'extraRespawns':
                    b.extraRespawns += v;
                    break;
                case 'healPerWavePct':
                    b.healPerWavePct += v;
                    break;
                case 'lastVowDrPct':
                    b.lastVowDrPct += v;
                    break;
                case 'lastBreathFlag':
                    if (rank >= 1)
                        b.lastBreathFlag = true;
                    break;
                case 'vowOfIronFlag':
                    if (rank >= 1)
                        b.vowOfIronFlag = true;
                    break;
                case 'revenantFlag':
                    if (rank >= 1)
                        b.revenantFlag = true;
                    break;
                case 'eternalBastionFlag':
                    if (rank >= 1)
                        b.eternalBastionFlag = true;
                    break;
                // ── CUNNING v2 ──
                case 'xpPct':
                    b.xpPct += v;
                    break;
                case 'recyclingPct':
                    b.recyclingPct += v;
                    break;
                case 'chanceToDoubleRecycle':
                    b.chanceToDoubleRecycle += v;
                    break;
                case 'spellNoCdChance':
                    b.spellNoCdChance += v;
                    break;
                case 'runeDropPct':
                    b.runeDropPct += v;
                    break;
                case 'bonusMovesPerWave':
                    b.bonusMovesPerWave += v;
                    break;
                case 'extraBookBan':
                    b.extraBookBan += v;
                    break;
                case 'bookRerolls':
                    b.bookRerolls += v;
                    break;
                case 'talentRerolls':
                    b.talentRerolls += v;
                    break;
                case 'shrineDiscountPct':
                    b.shrineDiscountPct += v;
                    break;
                case 'extraBoonSlot':
                    b.extraBoonSlot += v;
                    break;
                case 'invSlotBonus':
                    b.invSlotBonus += v;
                    break;
                case 'foresightExtraWaves':
                    b.foresightExtraWaves += v;
                    break;
                case 'premonitionFlag':
                    if (rank >= 1)
                        b.premonitionFlag = true;
                    break;
                case 'autoRunPlusFlag':
                    if (rank >= 1)
                        b.autoRunPlusFlag = true;
                    break;
                case 'tributeFlag':
                    if (rank >= 1)
                        b.tributeFlag = true;
                    break;
                // ── INVOCATIONS ──
                case 'avengingWrathFlag':
                    if (rank >= 1)
                        b.avengingWrathFlag = true;
                    break;
                case 'aegisOfLightFlag':
                    if (rank >= 1)
                        b.aegisOfLightFlag = true;
                    break;
                case 'timePullFlag':
                    if (rank >= 1)
                        b.timePullFlag = true;
                    break;
            }
        }
    }
    // Round vendor locks to nearest integer (per-rank fractional rounds at the boundary)
    b.vendorLocks = Math.round(b.vendorLocks);
    // Clamp bonusMovesPerWave to +2 (tree has two distinct +1 nodes, but the gameplay
    // contract is "max +2 from tree" — guards against future stacking exploits).
    b.bonusMovesPerWave = Math.min(2, b.bonusMovesPerWave);
    return b;
}
//# sourceMappingURL=passiveTree.js.map